import {
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Fab,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Add, Edit, Search } from "@mui/icons-material";
import Navbar from "../components/Navbar";
import { useAccount, useSecretNetworkClient } from "../models/account";
import useIsMobile from "../utils/useIsMobile";
import { useCallback, useEffect, useState } from "react";
import {
  cancelP2PDeal,
  cancelP2PPost,
  createP2PPost,
  customerDeposit,
  dealerDeposit,
  disputeDeal,
  fetchMyPaymentInfo,
  fetchP2PConfigs,
  fetchP2PPosts,
  P2PConfig,
  P2PDeal,
  P2PPaymentInfo,
  P2PPost,
  registerP2PPaymentInfo,
  resolveDeal,
  setP2PViewingKey,
} from "../models/p2p";
import AlertModal from "../components/AlertModal";
import { useFinaTokenInfo } from "../models/finaTokenInfo";
import PostCard, { StyledBadge } from "../components/P2P/PostCard";
import EnterDealModal from "../components/P2P/EnterDealModal";
import CreateP2PPostModal from "../components/P2P/CreateP2PPostModal";
import RegisterP2PModal from "../components/P2P/RegisterP2PModal";
import { formatNumber } from "../utils/formatNumber";
import MyDealsModal from "../components/P2P/MyDealsModal";
import BankTransferModal from "../components/P2P/BankTransferModal";
import Image from "next/image";
import getP2PNotifications from "../utils/getP2PNotifications";

export default function P2P() {
  const isMobile = useIsMobile();
  const { account, connect } = useAccount();
  const { finaToken, getFinaToken } = useFinaTokenInfo();
  const secretjs = useSecretNetworkClient();

  const [openCreatePostModal, setOpenCreatePostModal] = useState(false);
  const [openRegisterModal, setOpenRegisterModal] = useState(false);

  const [p2pConfigs, setP2PConfigs] = useState<P2PConfig[]>([]);
  const [currentP2PConfig, setCurrentP2PConfig] = useState<P2PConfig>();
  const [paymentInfo, setPaymentInfo] = useState<P2PPaymentInfo>();
  const [isBuy, setIsBuy] = useState(true);
  const [posts, setPosts] = useState<P2PPost[]>([]);
  const [dealingPost, setDealingPost] = useState<P2PPost>();
  const [myDealsPost, setMyDealsPost] = useState<P2PPost>();
  const [paymentDetailDeal, setPaymentDetailDeal] = useState<P2PDeal>();

  const [keyNotSet, setKeyNotSet] = useState(false);
  const [keyLoading, setKeyLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    secretjs &&
      fetchP2PConfigs(secretjs).then((result) => {
        setP2PConfigs(result);
        setCurrentP2PConfig(result[0]);
      });
  }, [secretjs]);

  const refetch = useCallback(() => {
    if (account.address && secretjs && currentP2PConfig) {
      if (finaToken.viewingKey) {
        setKeyNotSet(false);
        fetchP2PPosts(
          {
            contract_address: currentP2PConfig?.contract_address,
            code_hash: currentP2PConfig?.code_hash,
          },
          secretjs
        ).then((ps) => {
          setPosts(ps);
          setMyDealsPost((mdp) =>
            ps.find((p: any) => p.post_id === mdp?.post_id)
          );
        });
        fetchMyPaymentInfo(
          {
            contract_address: currentP2PConfig?.contract_address,
            code_hash: currentP2PConfig?.code_hash,
            address: account.address,
            viewing_key: finaToken.viewingKey,
          },
          secretjs
        )
          .then(setPaymentInfo)
          .catch((err) => {
            console.log(err);
            if (err.message === "Generic error: Viewing key not match") {
              setKeyNotSet(true);
            } else if (
              err.message === "Generic error: No payment info is found"
            ) {
              setOpenRegisterModal(true);
            }
          });
      } else {
        setKeyNotSet(true);
      }
    }
  }, [currentP2PConfig, secretjs, account, finaToken.viewingKey]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (success || error) {
      refetch();
    }
  }, [success, error, refetch]);

  const allPosts = posts.filter(
    (p) =>
      (isBuy ? !p.is_dealer_buy : p.is_dealer_buy) &&
      p.deal_token.address === currentP2PConfig?.deal_token?.address &&
      [
        p.settle_currency.contact,
        p.settle_currency.currency,
        p.settle_currency.method,
        p.post_id,
        p.dealer,
      ]
        .map((a) => a.toLowerCase().includes(search.toLowerCase()))
        .includes(true) &&
      (p.state === "open" ||
        p.dealer === account.address ||
        !!p.deals.find((d) => d.customer === account.address))
  );

  const sortOptions = [
    "Date: New to Old",
    "Date: Old to New",
    "Price: Low to High",
    "Price: High to Low",
  ];
  const [sortBy, setSortBy] = useState(sortOptions[0]);

  const myPosts = allPosts
    .filter(
      (p) =>
        p.dealer === account.address ||
        !!p.deals.find((d) => d.customer === account.address)
    )
    .sort((a, b) => {
      if (sortBy === "Date: New to Old") {
        return Number(b.post_id) - Number(a.post_id);
      } else if (sortBy === "Date: Old to New") {
        return Number(a.post_id) - Number(b.post_id);
      } else if (sortBy === "Price: Low to High") {
        return Number(a.settle_price) - Number(b.settle_price);
      } else if (sortBy === "Price: High to Low") {
        return Number(b.settle_price) - Number(a.settle_price);
      }
      return 0;
    });
  const openPosts = allPosts
    .filter(
      (p) =>
        currentP2PConfig?.deal_token?.address === p.deal_token?.address &&
        p.dealer !== account.address &&
        !p.deals.find((d) => d.customer === account.address)
    )
    .sort((a, b) => {
      if (sortBy === "Date: New to Old") {
        return Number(b.post_id) - Number(a.post_id);
      } else if (sortBy === "Date: Old to New") {
        return Number(a.post_id) - Number(b.post_id);
      } else if (sortBy === "Price: Low to High") {
        return Number(a.settle_price) - Number(b.settle_price);
      } else if (sortBy === "Price: High to Low") {
        return Number(b.settle_price) - Number(a.settle_price);
      }
      return 0;
    });

  const notifications = getP2PNotifications(p2pConfigs, posts, account.address);

  const myBuyPosts = notifications.filter(
    (n) =>
      !n.post.is_dealer_buy &&
      n.post.deal_token.address === currentP2PConfig?.deal_token?.address &&
      (n.post.dealer === account.address ||
        !!n.post.deals.find((d) => d.customer === account.address))
  );
  const mySellPosts = notifications.filter(
    (n) =>
      n.post.is_dealer_buy &&
      n.post.deal_token.address === currentP2PConfig?.deal_token?.address &&
      (n.post.dealer === account.address ||
        !!n.post.deals.find((d) => d.customer === account.address))
  );

  const postCardActions = (p: P2PPost) => {
    if (p.dealer === account.address && p.state === "pend_dealer_deposit") {
      return {
        buttonText: `Deposit ${formatNumber(
          p.amount / 10 ** (currentP2PConfig?.decimals || 6)
        )} ${currentP2PConfig?.name}`,
        onButtonClick: async () => {
          try {
            await dealerDeposit(
              {
                contract_address: currentP2PConfig?.contract_address || "",
                address: account.address,
                token_address: currentP2PConfig?.deal_token.address || "",
                token_code_hash: currentP2PConfig?.deal_token.code_hash || "",
                amount: String(p.amount),
                post_id: p.post_id,
              },
              secretjs!
            );
            setSuccess(
              `You have successfully deposited ${formatNumber(
                p.amount / 10 ** (currentP2PConfig?.decimals || 6)
              )} ${currentP2PConfig?.name} to your trade!`
            );
          } catch (err: any) {
            setError(err.message);
          }
        },
        buttonDisabledText: "",
        altButtonText: "Cancel",
        onAltButtonClick: p.deals?.length
          ? undefined
          : async () => {
              try {
                await cancelP2PPost(
                  {
                    contract_address: currentP2PConfig?.contract_address || "",
                    code_hash: currentP2PConfig?.code_hash || "",
                    address: account.address,
                    post_id: p.post_id,
                  },
                  secretjs!
                );
                setSuccess("You have successfully canceled your post!");
              } catch (err: any) {
                setError(err.message);
              }
            },
        altButtonDisabledText: "",
      };
    } else if (
      !!p.deals.find(
        (d) =>
          d.customer === account.address && d.state === "pend_customer_deposit"
      )
    ) {
      const deal = p.deals.find(
        (d) =>
          d.customer === account.address && d.state === "pend_customer_deposit"
      );
      return {
        buttonText: `Deposit ${formatNumber(
          (deal?.amount || 0) / 10 ** (currentP2PConfig?.decimals || 6)
        )} ${currentP2PConfig?.name}`,
        onButtonClick: async () => {
          try {
            const deal = p.deals.find(
              (d) =>
                d.customer === account.address &&
                d.state === "pend_customer_deposit"
            );
            await customerDeposit(
              {
                contract_address: currentP2PConfig?.contract_address || "",
                address: account.address,
                token_address: currentP2PConfig?.deal_token.address || "",
                token_code_hash: currentP2PConfig?.deal_token.code_hash || "",
                amount: String(deal?.amount),
                deal_id: deal?.deal_id || "",
              },
              secretjs!
            );
            setSuccess(
              `You have successfully deposited ${formatNumber(
                (deal?.amount || 0) / 10 ** (currentP2PConfig?.decimals || 6)
              )} ${currentP2PConfig?.name} to your trade!`
            );
          } catch (err: any) {
            setError(err.message);
          }
        },
        buttonDisabledText: "",
        altButtonText: "Cancel",
        altButtonDisabledText:
          Number(deal?.expiry || 0) * 1000 > Date.now()
            ? "You can cancel the trade after it expires"
            : "",
        onAltButtonClick: async () => {
          try {
            await cancelP2PDeal(
              {
                contract_address: currentP2PConfig?.contract_address || "",
                code_hash: currentP2PConfig?.code_hash || "",
                address: account.address,
                deal_id: deal?.deal_id || "",
              },
              secretjs!
            );
            setSuccess("You have successfully canceled your trade!");
          } catch (err: any) {
            setError(err.message);
          }
        },
      };
    } else if (
      !!p.deals.find(
        (d) =>
          d.customer === account.address &&
          d.state === "pend_dealer_bank_transfer"
      )
    ) {
      const deal = p.deals.find(
        (d) =>
          d.customer === account.address &&
          d.state === "pend_dealer_bank_transfer"
      );
      return {
        altButtonText: "Cancel",
        onAltButtonClick: async () => {
          try {
            await cancelP2PDeal(
              {
                contract_address: currentP2PConfig?.contract_address || "",
                code_hash: currentP2PConfig?.code_hash || "",
                address: account.address,
                deal_id: deal?.deal_id || "",
              },
              secretjs!
            );
            setSuccess("You have successfully canceled your trade!");
          } catch (err: any) {
            setError(err.message);
          }
        },
        altButtonDisabledText:
          Number(deal?.expiry || 0) * 1000 > Date.now()
            ? "You can cancel the trade after it expires"
            : "",
      };
    } else if (
      !!p.deals.find(
        (d) =>
          d.customer === account.address &&
          d.state === "pend_customer_bank_transfer"
      )
    ) {
      const deal = p.deals.find(
        (d) =>
          d.customer === account.address &&
          d.state === "pend_customer_bank_transfer"
      );
      return {
        onButtonClick: () => setPaymentDetailDeal(deal),
        buttonText: "Payment Details",
        altButtonText: "Cancel",
        onAltButtonClick: async () => {
          try {
            await cancelP2PDeal(
              {
                contract_address: currentP2PConfig?.contract_address || "",
                code_hash: currentP2PConfig?.code_hash || "",
                address: account.address,
                deal_id: deal?.deal_id || "",
              },
              secretjs!
            );
            setSuccess("You have successfully canceled your trade!");
          } catch (err: any) {
            setError(err.message);
          }
        },
        altButtonDisabledText:
          Number(deal?.expiry || 0) * 1000 > Date.now()
            ? "You can cancel the trade after it expires"
            : "",
      };
    } else if (
      !!p.deals.find(
        (d) =>
          d.customer === account.address && d.state === "pend_dealer_sign_off"
      )
    ) {
      const deal = p.deals.find(
        (d) =>
          d.customer === account.address && d.state === "pend_dealer_sign_off"
      );
      return {
        buttonText: "Resolve",
        onButtonClick: async () => {
          try {
            await resolveDeal(
              {
                contract_address: currentP2PConfig?.contract_address || "",
                code_hash: currentP2PConfig?.code_hash || "",
                address: account.address,
                deal_id: deal?.deal_id || "",
              },
              secretjs!
            );
            setSuccess("You have successfully resolved your deal!");
          } catch (err: any) {
            setError(err.message);
          }
        },
        buttonDisabledText:
          Number(deal?.expiry || 0) * 1000 > Date.now()
            ? "You can resolve the trade after it expires"
            : "",
      };
    } else if (p.dealer === account.address && p.state === "open") {
      return {
        onButtonClick: () => setMyDealsPost(p),
        buttonText: `My Deals (${p.deals?.length})`,
        buttonBadge: notifications.filter((n) => n.post.post_id === p.post_id)
          .length,
        altButtonText: "Cancel",
        onAltButtonClick: p.deals?.length
          ? undefined
          : async () => {
              try {
                await cancelP2PPost(
                  {
                    contract_address: currentP2PConfig?.contract_address || "",
                    code_hash: currentP2PConfig?.code_hash || "",
                    address: account.address,
                    post_id: p.post_id,
                  },
                  secretjs!
                );
                setSuccess("You have successfully canceled your post!");
              } catch (err: any) {
                setError(err.message);
              }
            },
      };
    } else if (
      !!p.deals.find(
        (d) =>
          d.customer === account.address && d.state === "pend_customer_sign_off"
      )
    ) {
      const deal = p.deals.find(
        (d) =>
          d.customer === account.address && d.state === "pend_customer_sign_off"
      )!;
      return {
        onButtonClick: async () => {
          try {
            await resolveDeal(
              {
                contract_address: currentP2PConfig?.contract_address || "",
                code_hash: currentP2PConfig?.code_hash || "",
                address: account.address,
                deal_id: deal?.deal_id,
              },
              secretjs!
            );
            setSuccess(`You have successfully signed off the trade!`);
          } catch (err: any) {
            setError(err.message);
          }
        },
        buttonText: "Sign Off",
        altButtonText: "Dispute",
        onAltButtonClick: async () => {
          try {
            await disputeDeal(
              {
                contract_address: currentP2PConfig?.contract_address || "",
                code_hash: currentP2PConfig?.code_hash || "",
                address: account.address,
                deal_id: deal?.deal_id,
              },
              secretjs!
            );
            setSuccess(
              `You have raised a dispute to the trade. Please create a ticket <a href="https://discord.com/channels/952397096722505768/1322233983391498324" target="_blank">here</a> to contact moderator.`
            );
          } catch (err: any) {
            setError(err.message);
          }
        },
      };
    } else if (
      !!p.deals.find(
        (d) => d.customer === account.address && d.state === "dispute"
      )
    ) {
      return {
        altButtonText: "Create Ticket",
        onAltButtonClick: () =>
          window.open(
            "https://discord.com/channels/952397096722505768/1322233983391498324",
            "_blank"
          ),
      };
    } else {
      return {};
    }
  };

  const [myTradesOnly, setMyTradesOnly] = useState(false);

  return secretjs ? (
    <>
      <Stack
        bgcolor={(theme) => theme.palette.background.default}
        minHeight="100vh"
        pb={10}
      >
        <Navbar
          activeTab="/p2p"
          notifications={notifications}
          onNotificationClick={(n) => {
            setMyDealsPost(n.post);
          }}
        />
        <Container sx={{ mt: { md: 8, xs: 4 }, mb: 8 }} maxWidth="md">
          <Stack direction="row" justifyContent="space-between" mb={4}>
            <Tabs
              value={currentP2PConfig?.deal_token?.address}
              onChange={(e, v) => {
                console.log({ p2pConfigs, v, e });
                setCurrentP2PConfig(
                  p2pConfigs.find((p) => p.deal_token?.address === v)
                );
              }}
            >
              {p2pConfigs.map((p) => (
                <Tab
                  key={p.deal_token?.address}
                  label={p.name}
                  value={p.deal_token?.address}
                />
              ))}
            </Tabs>
            <ToggleButtonGroup
              color="primary"
              value={isBuy}
              exclusive
              onChange={(e, v) => v !== null && setIsBuy(v)}
            >
              <ToggleButton sx={{ width: 100 }} value={true}>
                <StyledBadge badgeContent={myBuyPosts.length} color="secondary">
                  Buy
                </StyledBadge>
              </ToggleButton>
              <ToggleButton sx={{ width: 100 }} value={false}>
                <StyledBadge
                  badgeContent={mySellPosts.length}
                  color="secondary"
                >
                  Sell
                </StyledBadge>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <TextField
            sx={{ mb: 2 }}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            placeholder="Search by Payment Method, Currency, Trade ID or Trader Address"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            my={2}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={myTradesOnly}
                  onChange={(e, c) => setMyTradesOnly(c)}
                />
              }
              label="Show My Trades Only"
            />
            <FormControl>
              <InputLabel>Sort By</InputLabel>
              <Select
                label="Sort By"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                size="small"
              >
                {sortOptions.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {/* {myPosts.length ? (
            <Typography variant="h5" mb={4}>
              My Trades
            </Typography>
          ) : null} */}
          {myPosts.map((p) => (
            <PostCard
              key={p.post_id}
              highlighted
              post={p}
              config={currentP2PConfig}
              isMobile={isMobile}
              {...postCardActions(p)}
            />
          ))}
          {!(openPosts.length + myPosts.length) && (
            <Typography variant="h5" mb={4}>
              No Open Trades
            </Typography>
          )}
          {!myTradesOnly &&
            openPosts.map((p) => (
              <PostCard
                key={p.post_id}
                post={p}
                config={currentP2PConfig}
                isMobile={isMobile}
                onButtonClick={() => {
                  if (!paymentInfo?.method) {
                    setOpenRegisterModal(true);
                  } else {
                    setDealingPost(p);
                  }
                }}
              />
            ))}
        </Container>
      </Stack>
      {paymentInfo?.method ? (
        <Fab
          variant="extended"
          color="primary"
          sx={{ position: "fixed", right: 40, bottom: 100 }}
          onClick={() => {
            setOpenCreatePostModal(true);
          }}
        >
          <Add sx={{ mr: 1 }} />
          Create Post
        </Fab>
      ) : null}
      <Fab
        variant="extended"
        color="secondary"
        sx={{ position: "fixed", right: 40, bottom: 40 }}
        disabled={keyLoading}
        onClick={async () => {
          try {
            if (!!currentP2PConfig?.query_auth && secretjs) {
              if (keyNotSet) {
                setKeyLoading(true);
                let newKey;
                if (!finaToken.viewingKey) {
                  newKey = await getFinaToken(true);
                }
                await setP2PViewingKey(
                  {
                    address: account.address,
                    viewing_key: newKey?.viewingKey || finaToken.viewingKey,
                    query_auth: currentP2PConfig?.query_auth,
                  },
                  secretjs
                );
                refetch();
                setKeyLoading(false);
              } else {
                setOpenRegisterModal(true);
              }
            }
          } catch (err: any) {
            setKeyLoading(false);
            setError(err.message);
          }
        }}
      >
        <Edit sx={{ mr: 1 }} />
        {keyNotSet ? (
          keyLoading ? (
            <CircularProgress sx={{ mx: 1.5 }} size={25} />
          ) : (
            "Set Viewing Key"
          )
        ) : (
          "Payment Info"
        )}
      </Fab>
      <CreateP2PPostModal
        open={openCreatePostModal}
        onClose={() => setOpenCreatePostModal(false)}
        config={currentP2PConfig}
        paymentInfo={paymentInfo}
        onEditPaymentInfo={() => {
          setOpenCreatePostModal(false);
          setOpenRegisterModal(true);
        }}
        refetch={refetch}
      />
      <RegisterP2PModal
        open={openRegisterModal}
        onClose={() => setOpenRegisterModal(false)}
        config={currentP2PConfig}
        paymentInfo={paymentInfo}
        refetch={refetch}
      />
      <EnterDealModal
        open={!!dealingPost}
        onClose={() => setDealingPost(undefined)}
        post={dealingPost}
        config={currentP2PConfig}
        refetch={refetch}
      />
      <MyDealsModal
        open={!!myDealsPost}
        onClose={() => setMyDealsPost(undefined)}
        post={myDealsPost}
        config={currentP2PConfig}
        refetch={refetch}
        {...(myDealsPost && myDealsPost?.dealer !== account.address
          ? postCardActions(myDealsPost)
          : {})}
      />
      <BankTransferModal
        open={!!paymentDetailDeal}
        onClose={() => setPaymentDetailDeal(undefined)}
        deal={paymentDetailDeal}
        config={currentP2PConfig}
        refetch={refetch}
      />
      <AlertModal
        type={success ? "success" : "error"}
        title={success ? "Success" : "Error"}
        html={success || error}
        open={!!(success || error)}
        onClose={() => {
          setSuccess("");
          setError("");
        }}
      />
    </>
  ) : (
    <Stack
      bgcolor={(theme) => theme.palette.background.default}
      minHeight="100vh"
    >
      <Navbar activeTab="/p2p" />
      <img
        style={{ position: "absolute", left: 0, top: 60, width: "30vw" }}
        alt="p2p"
        src={"/p2p/left.svg"}
      />
      <img
        style={{ position: "absolute", right: 0, top: 60, width: "30vw" }}
        alt="p2p"
        src={"/p2p/right.svg"}
      />
      <Stack
        flex={1}
        alignItems="center"
        justifyContent="center"
        sx={{ px: 4, zIndex: 10000 }}
      >
        <Stack maxWidth={600}>
          <Typography mb={2} variant="h2" className="gradient" align="center">
            {/* Fina P2P */}
            Fina P2P
          </Typography>
          <Typography align="center" variant="body2" mb={4}>
            Thank you for using Fina P2P! Please follow the instructions{" "}
            <a
              href="https://docs.fina.cash/fina-p2p/features/getting-started"
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>{" "}
            to access Fina P2P on Secret Network:
          </Typography>
        </Stack>
        <Button
          size="large"
          onClick={connect}
          startIcon={
            isMobile ? (
              <Image
                alt="Fina"
                src={require("../assets/img/wallets/fina.png")}
                width={20}
                height={20}
                style={{ borderRadius: 4 }}
              />
            ) : (
              <Image
                alt="Keplr"
                src={require("../assets/img/wallets/keplr.png")}
                width={20}
                height={20}
                style={{ borderRadius: 4 }}
              />
            )
          }
        >
          Connect Wallet
        </Button>
      </Stack>
    </Stack>
  );
}
